/**
 * Allocates selected travellers (employees and guests) to rooms.
 *
 * Distribution logic:
 * - Uses the adult count from each room's occupancy to determine allocation
 * - Employees are assigned first, then guests fill remaining spots
 * - Travellers are distributed sequentially across rooms based on adult capacity
 *
 * @example
 * // rooms with adults: [2, 1, 1], 3 employees, 1 guest -> room 0: [emp1, emp2], room 1: [emp3], room 2: [guest-0]
 * // rooms with adults: [1, 1, 1, 1], 4 employees -> room 0: [emp1], room 1: [emp2], room 2: [emp3], room 3: [emp4]
 */
export function allocateTravellersToRooms<T extends App.Occupants | App.RoomOccupants>(
  rooms: Array<T>,
  selectedTravellerEmployeeIds: Array<string> | undefined,
  selectedTravellerGuestIds: Array<string> | undefined,
): Array<T & { employeeIds?: Array<string>; guestIds?: Array<string> }> {
  if (!selectedTravellerEmployeeIds?.length && !selectedTravellerGuestIds?.length) {
    return rooms
  }
  const employeeIds = selectedTravellerEmployeeIds ?? []
  const guestIds = selectedTravellerGuestIds ?? []
  const totalTravellers = employeeIds.length + guestIds.length

  if (totalTravellers === 0) {
    return rooms
  }

  let employeeIndex = 0
  let guestIndex = 0

  return rooms.map((room) => {
    // Because we use the normaliseOccupancies function during search,
    // it's safe to use the adult count from the room's occupancy to assign employees and guests.
    const adultsInRoom = room.adults

    const roomEmployeeIds = employeeIds.slice(
      employeeIndex,
      employeeIndex + adultsInRoom
    )
    const assignedEmployees = roomEmployeeIds.length

    employeeIndex += assignedEmployees

    const neededGuests = adultsInRoom - assignedEmployees
    const roomGuestIds = guestIds.slice(
      guestIndex,
      guestIndex + neededGuests
    )

    guestIndex += roomGuestIds.length

    return {
      ...room,
      ...(roomEmployeeIds.length > 0 && {
        employeeIds: roomEmployeeIds,
      }),
      ...(roomGuestIds.length > 0 && {
        guestIds: roomGuestIds,
      }),
    }
  })
}
